import * as Sentry from '@sentry/react';
import {StandardSchemaV1} from '@standard-schema/spec';
import * as Either from 'effect/Either';
import {isDeepEqual} from '~/library/toolbox';

// export function schemaFromStandardV1<T>(standard: StandardSchemaV1<unknown, T>): Schema.Schema<T, unknown, never> {
//     return Schema.Unknown.pipe(
//         Schema.transformOrFail(
//             Schema.declare((thing: unknown): thing is T => {
//                 const result = standard['~standard'].validate(thing);
//                 if (result instanceof Promise) {
//                     return false;
//                 }
//                 return 'value' in result;
//             }),
//             {
//                 strict: true,
//                 encode: (): Effect<never, ParseIssue, never> => {
//                     throw new Error("Can't encode standard schema");
//                 },
//                 decode: (fromA, _opt, ast): Effect<T, ParseIssue, never> => {
//                     const result = standard['~standard'].validate(fromA);
//                     if (result instanceof Promise) {
//                         return fail(new ParseResult.Type(ast, 'StandardSchemaV1 is async'));
//                     }
//                     if (result.issues) {
//                         return fail(new ParseResult.Type(ast, 'StandardSchemaV1 got issues'));
//                     }
//                     return succeed(result.value);
//                 },
//             }
//         ),
//         Schema.asSchema
//     );
// }

type E = DecoderError;
class DecoderError extends global.Error {
    readonly name: string = 'DecodingError';

    readonly issues!: StandardSchemaV1.Issue[];

    private constructor() {
        super();
    }

    /**
     * @example
     * Decoder.Error.make(new Error(), issues)
     * Decoder.Error.make(new Error("First line of final message"), issues)
     */
    static make(error: Error, issues: readonly StandardSchemaV1.Issue[]) {
        Object.setPrototypeOf(error, this.prototype);
        Object.assign(error, {
            name: this.prototype.name,
            message: [...[error.message].filter(m => !!m), ...issues.map(i => i.message)].join('\n'),
            issues: issues,
        });
        return error as DecoderError;
    }

    static is(thing: unknown): thing is DecoderError {
        return thing instanceof DecoderError;
    }
}

export namespace Decoder {
    export const make = makeDecoderSync;
    export const makeAsync = makeDecoderAsync;
    export const makeLax = makeDecoderLax;
    export const Error = DecoderError;

    export const morph = makeMorph;
    export const prepare = makePrepare;

    interface SyncRunOpt<T> {
        get: () => T | E;
        or: <F>(fallback: F) => T | F;
        orThrow: () => T;
        toEither: () => Either.Either<T, E>;
    }

    export function run<T, I = unknown>(schema: StandardSchemaV1<I, T>, subject: I): SyncRunOpt<T> {
        const decoder = make(schema);
        return {
            get: () => decoder(subject),
            or: fallback => decoder.or(fallback)(subject),
            orThrow: () => decoder.orThrow()(subject),
            toEither: () => decoder.toEither()(subject),
        };
    }

    interface AsyncRunOpt<T> {
        get: () => Promise<T | E>;
        or: <F>(fallback: F) => Promise<T | F>;
        orThrow: () => Promise<T>;
        toEither: () => Promise<Either.Either<T, E>>;
    }

    export function runAsync<T, I = unknown>(schema: StandardSchemaV1<I, T>, subject: I): AsyncRunOpt<T> {
        const decoder = makeAsync(schema);
        return {
            get: () => decoder(subject),
            or: fallback => decoder.or(fallback)(subject),
            orThrow: () => decoder.orThrow()(subject),
            toEither: () => decoder.toEither()(subject),
        };
    }

    type ArkLike<T> = {
        allows: (x: unknown) => x is T;
    } & StandardSchemaV1<T, unknown>;

    type IoTsLike<T> = {
        is: (x: unknown) => x is T;
    } & StandardSchemaV1<unknown, T>;

    export function check<T>(schema: ArkLike<T>): (input: unknown) => input is T;
    export function check<T>(schema: IoTsLike<T>): (input: unknown) => input is T;
    export function check<T>(schema: StandardSchemaV1<unknown, T>): (input: unknown) => input is T;
    export function check<T>(
        schema: StandardSchemaV1<unknown, T> | ArkLike<T> | IoTsLike<T>
    ): (input: unknown) => input is T {
        if ('is' in schema) {
            return input => schema.is(input);
        }
        if ('allows' in schema) {
            return input => schema.allows(input);
        }
        return (input: unknown): input is T => {
            const output = run(schema, input).toEither();
            if (Either.isRight(output)) {
                return isDeepEqual(input, output.right);
            }
            return false;
        };
    }
}

function makeDecoderSync<T>(schema: StandardSchemaV1<unknown, T>): {
    (subject: unknown): T | E;
    or: <F>(fallback: F) => (subject: unknown) => T | F;
    orThrow: () => (subject: unknown) => T;
    toEither: () => (subject: unknown) => Either.Either<T, E>;
} {
    const impl = (subject: unknown) => {
        const result = schema['~standard'].validate(subject);
        if (result instanceof Promise) {
            throw new Error('Sync Decoder encountered a promise');
        }
        return wrapStandardResult(result);
    };

    const decode = (subject: unknown) => impl(subject).get();

    decode.or =
        <F>(fallback: F) =>
        (subject: unknown): T | F =>
            impl(subject).or(fallback);

    decode.orThrow =
        () =>
        (subject: unknown): T =>
            impl(subject).orThrow();

    decode.toEither =
        () =>
        (subject: unknown): Either.Either<T, E> =>
            impl(subject).toEither();

    return decode;
}

function makeDecoderAsync<T>(schema: StandardSchemaV1<unknown, T>): {
    (subject: unknown): Promise<T | E>;
    or: <F>(fallback: F) => (subject: unknown) => Promise<T | F>;
    orThrow: () => (subject: unknown) => Promise<T>;
    toEither: () => (subject: unknown) => Promise<Either.Either<T, E>>;
} {
    const impl = async (subject: unknown) => {
        const result = await schema['~standard'].validate(subject);
        return wrapStandardResult(result);
    };

    const decode = (subject: unknown) => impl(subject).then(s => s.get());

    decode.or =
        <F>(fallback: F) =>
        (subject: unknown) =>
            impl(subject).then(s => s.or(fallback));

    decode.orThrow = () => (subject: unknown) => impl(subject).then(s => s.orThrow());

    decode.toEither = () => (subject: unknown) => impl(subject).then(s => s.toEither());

    return decode;
}

function wrapStandardResult<T>(result: StandardSchemaV1.Result<T>): {
    get: () => T | E;
    or: <F>(fallback: F) => T | F;
    orThrow: () => T;
    toEither: () => Either.Either<T, E>;
} {
    if (result.issues) {
        const error = DecoderError.make(new Error(), result.issues);
        return {
            get: () => error,
            or: <U>(fallback: U) => fallback,
            orThrow: () => {
                throw error;
            },
            toEither: () => Either.left(error),
        };
    } else {
        const value = result.value;
        return {
            get: () => value,
            or: () => value,
            orThrow: () => value,
            toEither: () => Either.right(value),
        };
    }
}

function makeDecoderLax<L, S extends L>(
    SchemaLax: StandardSchemaV1<unknown, L>,
    SchemaStrict: StandardSchemaV1<unknown, S>,
    {laxToStrict, report = true}: {laxToStrict: (lax: L) => S; report?: boolean}
): StandardSchemaV1<unknown, S> {
    return makeValidation((value: unknown): StandardSchemaV1.Result<S> => {
        const strict = Decoder.run(SchemaStrict, value).get();
        if (!(strict instanceof Decoder.Error)) {
            return {value: strict};
        }
        const lax = Decoder.run(SchemaLax, value).get();
        if (lax instanceof Decoder.Error) {
            return {issues: lax.issues};
        }
        if (report) {
            Sentry.captureException(new Error('Strict validation failed but lax succeded', {cause: strict}), {
                level: 'log',
            });
        }
        try {
            return {value: laxToStrict(lax)};
        } catch (error) {
            if (error instanceof DecoderError) {
                return {issues: error.issues};
            }
            throw error;
        }
    });
}

function makeMorph<I, O>(schema: StandardSchemaV1<unknown, I>, morph: (input: I) => O): StandardSchemaV1<unknown, O> {
    return makeValidation((value: unknown): StandardSchemaV1.Result<O> => {
        const output1 = Decoder.run(schema, value).get();
        if (output1 instanceof Decoder.Error) {
            return {issues: output1.issues};
        }
        try {
            return {value: morph(output1)};
        } catch (error) {
            if (error instanceof Decoder.Error) {
                return {issues: error.issues};
            }
            throw error;
        }
    });
}

function makePrepare(preapre: (thing: unknown) => unknown) {
    return <T>(schema: StandardSchemaV1<unknown, T>) => {
        return makeValidation(value => {
            try {
                value = preapre(value);
            } catch (error) {
                if (DecoderError.is(error)) {
                    return {issues: error.issues};
                }
                throw error;
            }
            return schema['~standard'].validate(value);
        });
    };
}

function makeValidation<I, O>(
    validate: (input: unknown) => StandardSchemaV1.Result<O> | Promise<StandardSchemaV1.Result<O>>
): StandardSchemaV1<I, O> {
    return {
        '~standard': {
            version: 1,
            vendor: 'raison',
            validate,
        },
    };
}
